'use client';

import { marked } from 'marked';
import { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import './code-highlight.css';

function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  return tokens.map((token) => token.raw);
}

// Module-level plugin arrays so every ReactMarkdown render receives stable
// references — re-creating these per render would defeat downstream memo
// comparisons and re-run the (expensive) unified pipeline setup.
const REMARK_PLUGINS = [remarkGfm];
// rehype-highlight only highlights fenced blocks with an explicit
// ```lang tag (no auto-detection) — unlabeled blocks stay plain, which keeps
// streaming render cost flat and avoids mis-colored guesses.
const REHYPE_PLUGINS = [rehypeHighlight];

/**
 * Root wrapper for every rendered markdown tree.
 *
 * `whitespace-normal` is the important part, not cosmetics: the chat message
 * bubble sets `whitespace-pre-wrap` (correct for a USER's typed plain text,
 * which must keep its line breaks). Rendered markdown inherits that, and
 * react-markdown keeps the source newlines between sibling nodes as real text
 * nodes — so under `pre-wrap` every newline between `<li>`s and around block
 * elements paints as a visible blank line. That is what turned Meridian's
 * lists into a column of orphaned markers separated by big gaps. Resetting
 * whitespace here makes the renderer immune to whatever the container sets.
 *
 * The first/last-child margin reset keeps the tree flush with the bubble
 * padding instead of adding a leading blank line.
 */
const MARKDOWN_ROOT_CLASS =
  'whitespace-normal [&>*:first-child]:mt-0 [&>*:last-child]:mb-0';

/**
 * Shared list styling.
 *
 * `list-outside` (Tailwind's default, stated explicitly here) + left padding
 * is what gives a hanging marker. The previous `list-inside` put the marker in
 * the text flow, so any list item whose first child is a block element — every
 * item of a loose list, i.e. most of what Meridian emits — rendered "1." alone
 * on its own line above its own text.
 */
const LIST_CLASS = 'list-outside my-2 pl-5 space-y-1 marker:text-muted-foreground';

/**
 * List item.
 *
 * A loose list gives each `<li>` a `<p>` child; without zeroing that
 * paragraph's margin it stacks with the list's `space-y` and doubles every
 * gap. Nested lists tighten to `my-1` so a sub-list hugs its parent item.
 * Consecutive paragraphs inside one item keep real separation.
 */
const LIST_ITEM_CLASS =
  'pl-1 [&>p]:my-0 [&>p+p]:mt-2 [&>ul]:my-1 [&>ol]:my-1 [&>pre]:my-1.5';

// Custom components. Tailwind's preflight strips ALL default element styling
// (heading sizes, list markers, blockquote indents, hr borders), so anything
// not given a renderer here renders as undifferentiated body text — hence the
// full h1–h6 / blockquote / hr / img coverage rather than only h2+h3.
const markdownComponents: Components = {
  ul: ({ node: _node, children, className, ...props }) => (
    // A GFM task list carries `contains-task-list`; its items own their
    // checkbox, so suppress the markers and the marker gutter.
    <ul
      {...props}
      className={
        className?.includes('contains-task-list')
          ? 'my-2 pl-0 space-y-1 list-none'
          : `list-disc ${LIST_CLASS}`
      }
    >
      {children}
    </ul>
  ),
  // `start` (a list that begins at 3.) arrives in `props` — spreading it is
  // what keeps the numbering; the old renderer dropped it and restarted at 1.
  ol: ({ node: _node, children, ...props }) => (
    <ol {...props} className={`list-decimal ${LIST_CLASS}`}>
      {children}
    </ol>
  ),
  li: ({ node: _node, children, className, ...props }) =>
    className?.includes('task-list-item') ? (
      <li {...props} className={`flex items-start gap-2 list-none ${LIST_ITEM_CLASS}`}>
        {children}
      </li>
    ) : (
      <li {...props} className={LIST_ITEM_CLASS}>
        {children}
      </li>
    ),
  // GFM task-list checkbox: react-markdown renders a disabled input; give it a
  // size and stop it from being squashed by the flex row above.
  input: ({ node: _node, ...props }) => (
    <input {...props} className="mt-1 flex-shrink-0 accent-primary" />
  ),
  h1: ({ node: _node, children, ...props }) => (
    <h1 {...props} className="text-xl font-semibold mt-4 mb-2">
      {children}
    </h1>
  ),
  h2: ({ node: _node, children, ...props }) => (
    <h2 {...props} className="text-lg font-semibold mt-4 mb-2">
      {children}
    </h2>
  ),
  h3: ({ node: _node, children, ...props }) => (
    <h3 {...props} className="text-base font-semibold mt-3 mb-1.5">
      {children}
    </h3>
  ),
  h4: ({ node: _node, children, ...props }) => (
    <h4 {...props} className="text-sm font-semibold mt-3 mb-1">
      {children}
    </h4>
  ),
  h5: ({ node: _node, children, ...props }) => (
    <h5 {...props} className="text-sm font-semibold mt-2 mb-1">
      {children}
    </h5>
  ),
  h6: ({ node: _node, children, ...props }) => (
    <h6 {...props} className="text-sm font-semibold text-muted-foreground mt-2 mb-1">
      {children}
    </h6>
  ),
  p: ({ node: _node, children, ...props }) => (
    <p {...props} className="my-2 leading-relaxed">
      {children}
    </p>
  ),
  blockquote: ({ node: _node, children, ...props }) => (
    <blockquote
      {...props}
      className="my-2 border-l-2 border-border pl-3 text-muted-foreground italic"
    >
      {children}
    </blockquote>
  ),
  hr: ({ node: _node, ...props }) => <hr {...props} className="my-3 border-t border-border" />,
  // react-markdown v9+ no longer passes an `inline` prop — fenced blocks are
  // distinguished by the `language-*` / `hljs` className that rehype-highlight
  // leaves on them. Preserving `className` here is what carries the highlight
  // token classes through to the DOM; dropping it silently disables
  // highlighting. Inline code (no className) keeps the quiet pill styling.
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) =>
    className ? (
      // Fenced code: the surrounding <pre> (see the `pre` renderer below) owns
      // the box + scroll behavior. Here we only style the inner <code> so we
      // don't produce an invalid nested <pre><pre>.
      <code className={`font-mono ${className}`}>{children}</code>
    ) : (
      <code className="font-mono">{children}</code>
    ),
  // Fenced code block wrapper. Reset the inherited `whitespace-pre-wrap
  // break-words` from the message bubble with `whitespace-pre` so long lines
  // scroll inside `overflow-x-auto` instead of wrapping/garbling mid-token.
  // `meridian-hljs` scopes the light/dark token theme (code-highlight.css).
  pre: ({ children }) => (
    <pre className="meridian-hljs bg-muted p-2 rounded text-sm font-mono overflow-x-auto whitespace-pre my-2">
      {children}
    </pre>
  ),
  // GFM tables: wrap in a horizontally-scrollable container so wide tables
  // scroll instead of overflowing the chat column on mobile/iPad.
  table: ({ node: _node, children, ...props }) => (
    <div className="overflow-x-auto my-2">
      <table {...props} className="w-full border-collapse text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ node: _node, children, ...props }) => (
    <thead {...props} className="border-b border-border">
      {children}
    </thead>
  ),
  // `align` (from `|:---:|` column alignment) rides in on props.
  th: ({ node: _node, children, ...props }) => (
    <th {...props} className="px-3 py-1.5 text-left font-semibold whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ node: _node, children, ...props }) => (
    <td {...props} className="px-3 py-1.5 border-t border-border align-top">
      {children}
    </td>
  ),
  strong: ({ node: _node, children, ...props }) => (
    <strong {...props} className="font-semibold">
      {children}
    </strong>
  ),
  em: ({ node: _node, children, ...props }) => (
    <em {...props} className="italic">
      {children}
    </em>
  ),
  del: ({ node: _node, children, ...props }) => (
    <del {...props} className="line-through text-muted-foreground">
      {children}
    </del>
  ),
  img: ({ node: _node, ...props }) => (
    // Model-authored markdown can point at any remote src, so next/image is
    // not an option here.
    <img {...props} alt={props.alt ?? ''} className="my-2 max-w-full h-auto rounded" />
  ),
  a: ({ node: _node, href, children, ...props }) => (
    <a
      {...props}
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="text-primary underline underline-offset-2 decoration-2 hover:text-primary/90 hover:decoration-primary/80 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1 rounded"
    >
      {children}
    </a>
  ),
};

const MemoizedMarkdownBlock = memo(
  ({ content }: { content: string }) => {
    return (
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.content !== nextProps.content) return false;
    return true;
  }
);

MemoizedMarkdownBlock.displayName = 'MemoizedMarkdownBlock';

export const MemoizedMarkdown = memo(
  ({ content, id }: { content: string; id: string }) => {
    const blocks = useMemo(() => parseMarkdownIntoBlocks(content), [content]);

    if (blocks.length === 0) {
      return (
        <div className={MARKDOWN_ROOT_CLASS}>
          <ReactMarkdown
            remarkPlugins={REMARK_PLUGINS}
            rehypePlugins={REHYPE_PLUGINS}
            components={markdownComponents}
          >
            {content}
          </ReactMarkdown>
        </div>
      );
    }

    return (
      <div className={MARKDOWN_ROOT_CLASS}>
        {blocks.map((block, index) => (
          <MemoizedMarkdownBlock
            content={block}
            key={`${id}-block_${index}`}
          />
        ))}
      </div>
    );
  }
);

MemoizedMarkdown.displayName = 'MemoizedMarkdown';
